import React, { useContext, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Checkbox,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useNavigate } from "react-router-dom";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  KivuI18nContext,
  PAGE_PORTAL_PEOPLE_EMPLOYEES,
  PORTAL_PREFIX,
  localizedPath,
  useKivunovaTranslation,
  usePlatformConfig
} from "@kivunova/kivufrontendcommon";
import { formatUserRoleLabel } from "../Profile/utils/userRoleLabels";
import { Employee } from "./types/employees";
import { EmployeeTableProps } from "./types/employees";

type Order = "asc" | "desc";

const EmployeeTable: React.FC<EmployeeTableProps> = ({
  employees,
  title = "Employees",
  showPhoneColumn = true
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useKivunovaTranslation();
  const { config } = usePlatformConfig();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};

  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof Employee>("firstName");
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  /** Filter by search */
  const filteredList = useMemo(() => {
    if (!search.trim()) return employees;
    return employees.filter((employee) =>
      Object.values(employee)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [employees, search]);

  /** Sorting logic */
  const sortedList = useMemo(() => {
    return [...filteredList].sort((a, b) => {
      const aValue = a[orderBy] ?? "";
      const bValue = b[orderBy] ?? "";
      if (typeof aValue === "number" && typeof bValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }
      return order === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [filteredList, order, orderBy]);

  /** Pagination */
  const paginatedList = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedList.slice(start, start + itemsPerPage);
  }, [sortedList, page]);

  /** Handle selection */
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(paginatedList.map((e) => e.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((eid) => eid !== id) : [...prev, id]
    );
  };

  /** Handle sort toggle */
  const handleSort = (property: keyof Employee) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  /** Define columns based on screen size */
  const columns = matchMD
    ? [{ id: "firstName" as keyof Employee, label: t("name", "Name") }]
    : [
        {
          id: "firstName" as keyof Employee,
          label: t("first-name", "First Name")
        },
        {
          id: "lastName" as keyof Employee,
          label: t("last-name", "Last Name")
        },
        { id: "email" as keyof Employee, label: t("email", "Email") },
        ...(showPhoneColumn
          ? [{ id: "phone" as keyof Employee, label: t("phone", "Phone") }]
          : []),
        { id: "role" as keyof Employee, label: t("role", "Role") }
      ];

  return (
    <Box width="100%">
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        flexDirection={matchMD ? "column" : "row"}
        gap={2}
      >
        <Typography variant="h6" fontWeight="bold">
          {title}
        </Typography>

        <TextField
          size="small"
          placeholder={t("search-employees", "Search employees")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 200 }}
        />
      </Box>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  checked={
                    selectedIds.length === paginatedList.length &&
                    paginatedList.length > 0
                  }
                  indeterminate={
                    selectedIds.length > 0 &&
                    selectedIds.length < paginatedList.length
                  }
                  onChange={handleSelectAll}
                />
              </TableCell>

              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sortDirection={orderBy === column.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={() => handleSort(column.id)}
                    sx={{ fontSize: 13, fontWeight: 600 }}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}

              <TableCell sx={{ fontSize: 13, fontWeight: 600 }} align="center">
                {t("actions", "Actions")}
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedList.map((employee) => (
              <TableRow
                key={employee.id}
                hover
                sx={{
                  "&:hover": {
                    backgroundColor: "action.hover",
                    cursor: "pointer"
                  }
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    checked={selectedIds.includes(employee.id)}
                    onChange={() => handleSelectOne(employee.id)}
                  />
                </TableCell>

                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {!matchMD && (
                      <Avatar
                        src={employee.profileImage}
                        sx={{ width: 30, height: 30 }}
                      />
                    )}
                    <Typography variant="body2" sx={{ fontSize: 13 }}>
                      {matchMD
                        ? `${employee.firstName} ${employee.lastName}`
                        : employee.firstName}
                    </Typography>
                  </Stack>
                </TableCell>

                {!matchMD && (
                  <>
                    <TableCell sx={{ fontSize: 13 }}>
                      {employee.lastName ?? t("na", "N/A")}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {employee.email ?? t("na", "N/A")}
                    </TableCell>
                    {showPhoneColumn && (
                      <TableCell sx={{ fontSize: 13 }}>
                        {employee.phone || t("na", "N/A")}
                      </TableCell>
                    )}
                    <TableCell sx={{ fontSize: 13 }}>
                      {employee.role
                        ? formatUserRoleLabel(
                            employee.role,
                            t,
                            config?.businessType
                          )
                        : t("na", "N/A")}
                    </TableCell>
                  </>
                )}

                <TableCell align="center" sx={{ fontSize: 13 }}>
                  <IconButton
                    color="primary"
                    onClick={() =>
                      navigate(
                        localizedPath(
                          `/${PORTAL_PREFIX}/${PAGE_PORTAL_PEOPLE_EMPLOYEES}/${employee.id}`,
                          language,
                          defaultLang
                        )
                      )
                    }
                  >
                    <VisibilityOutlinedIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box display="flex" justifyContent="center" mt={3}>
        <Pagination
          variant="outlined"
          count={Math.ceil(sortedList.length / itemsPerPage)}
          page={page}
          onChange={(_, value) => setPage(value)}
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default EmployeeTable;
